import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Avatar,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Collapse,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { toast } from 'react-toastify';
import { updateUser } from '../../redux/reducers/userSlice';

const ProfileEditScreen = ({ history }) => {
  const { token, userInfo } = useSelector((state) => state.userInfo);
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [newProfile, setNewProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState(
    'https://your-default-profile-pic-url.com'
  );
  const dispatch = useDispatch();
  const [showSocialLinks, setShowSocialLinks] = useState(false);

  // Social links states
  const [socialLinks, setSocialLinks] = useState({
    twitter: '',
    instagram: '',
    linkedIn: '',
    facebook: '',
    tiktok: '',
    snapchat: '',
    reddit: '',
    pinterest: '',
    medium: '',
    tumblr: '',
  });

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setSocialLinks((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!username || !email || !/\S+@\S+\.\S+/.test(email)) {
      return false;
    }
    return true;
  };

  const saveProfile = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('newUserId', userInfo.id);
    formData.append('newName', name);
    formData.append('newUsername', username);
    formData.append('newWebsite', website);
    formData.append('newBio', bio);
    Object.entries(socialLinks).forEach(([key, value]) =>
      formData.append(`new${key.charAt(0).toUpperCase() + key.slice(1)}`, value)
    );

    if (newProfile) {
      formData.append('file', newProfile);
    }

    const updateProfile = async () => {
      await axios.put(
        'https://soapboxapi.megahoot.net/profile/edit',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      axios
        .get(`https://soapboxapi.megahoot.net/user/${username}`)
        .then((res) => {
          //setUser(res.data[0]);
          if (res.data) {
            const newUserinfo = res.data[0];

            dispatch(updateUser(newUserinfo));
            setProfilePic(
              `https://soapboxapi.megahoot.net/profile-pictures/${newUserinfo?.profilePic}`
            );
            setName(newUserinfo?.name);
            setUsername(newUserinfo?.username);
            setEmail(newUserinfo?.email);
            setBio(newUserinfo?.bio);
            setWebsite(newUserinfo.website);
            setSocialLinks({
              twitter: newUserinfo?.twitter,
              instagram: newUserinfo?.instagram,
              linkedIn: newUserinfo?.linkedIn,
              facebook: newUserinfo?.facebook,
              tiktok: newUserinfo?.tiktok,
              snapchat: newUserinfo?.snapchat,
              reddit: newUserinfo?.reddit,
              pinterest: newUserinfo?.pinterest,
              medium: newUserinfo?.medium,
              tumblr: newUserinfo?.tumblr,
            });
            // navigation.navigate('Home', { data: res.data });
            //saveUserProfile(JSON.stringify(res.data));
          }
        })
        .catch((err) => console.log(err));

      setLoading(false);
    };

    toast.promise(updateProfile, {
      pending: `Updating Profile`,
      success: `Profile updated successfully`,
      error: 'Please try again',
    });
  };

  const handleSubmit = () => {
    if (validate()) {
      saveProfile();
    }
  };

  useEffect(() => {
    setProfilePic(
      `https://soapboxapi.megahoot.net/profile-pictures/${userInfo?.profilePic}`
    );
    setName(userInfo?.name);
    setUsername(userInfo?.username);
    setEmail(userInfo?.email);
    setBio(userInfo?.bio);
    setWebsite(userInfo.website);
    setSocialLinks({
      twitter: userInfo?.twitter,
      instagram: userInfo?.instagram,
      linkedIn: userInfo?.linkedIn,
      facebook: userInfo?.facebook,
      tiktok: userInfo?.tiktok,
      snapchat: userInfo?.snapchat,
      reddit: userInfo?.reddit,
      pinterest: userInfo?.pinterest,
      medium: userInfo?.medium,
      tumblr: userInfo?.tumblr,
    });
  }, [userInfo]);

  return (
    <Box
      sx={{
        maxWidth: 600,
        margin: '0 auto',
        padding: 3,
        bgcolor: '#f9f9f9',
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h4" align="center" gutterBottom>
        Edit Profile
      </Typography>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Avatar
          src={profilePic}
          alt="Profile"
          sx={{ width: 100, height: 100, margin: '0 auto', mb: 1 }}
        />
        <input
          accept="image/*"
          id="file-input"
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              setProfilePic(URL.createObjectURL(file));
              setNewProfile(file);
            }
          }}
        />
        <label htmlFor="file-input">
          <IconButton component="span" color="primary">
            <PhotoCamera />
          </IconButton>
        </label>
      </Box>
      <Box
        component="form"
        onSubmit={(e) => e.preventDefault()}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <TextField
          label="Name"
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Username"
          variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <TextField
          label="Email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Bio"
          variant="outlined"
          multiline
          minRows={3}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
        />
        <TextField
          label="Website"
          variant="outlined"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
        <Button
          variant="outlined"
          onClick={() => setShowSocialLinks(!showSocialLinks)}
        >
          {showSocialLinks ? 'Hide Social Links' : 'Add Social Links'}
        </Button>
        <Collapse in={showSocialLinks}>
          {Object.keys(socialLinks).map((link) => (
            <TextField
              key={link}
              label={link.charAt(0).toUpperCase() + link.slice(1)}
              name={link}
              variant="outlined"
              value={socialLinks[link]}
              onChange={handleSocialChange}
              sx={{ mt: 2 }}
            />
          ))}
        </Collapse>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Profile'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileEditScreen;
